import React, { useState, useEffect, useCallback } from 'react';
import { quotations as qApi, orders as ordersApi } from '../utils/api';
import { useT } from '../i18n/I18nContext';
import ExportOptionsModal from './ExportOptionsModal';

// Dispara o download de um Blob (Excel / zip) criando um <a> temporário.
function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  a.parentNode.removeChild(a);
  URL.revokeObjectURL(url);
}

const SOURCE_META = {
  tech_pack: { icon: '📐', order: 0 },
  item:      { icon: '📦', order: 1 },
  quotation: { icon: '📎', order: 2 },
  supplier:  { icon: '🏭', order: 3 },
  agent:     { icon: '🕴️', order: 4 },
  payment:   { icon: '💰', order: 5 },
  financial: { icon: '🧾', order: 6 },
};
const IMG_RE = /\.(jpe?g|png|gif|webp|heic|bmp|svg)$/i;

// Painel "Downloads" — disponível em TODAS as fases (cotação / pedido / amostra).
// Lista cada anexo (fotos, docs por item, comprovantes, arquivos do fornecedor)
// com download individual, + "Baixar tudo (.zip)" e "Baixar Excel atualizado".
export default function DownloadsPanel({ quotation: q, toast, onUploaded }) {
  const t = useT();
  const [files, setFiles] = useState(null);
  const [loading, setLoading] = useState(true);
  const [zipping, setZipping] = useState(false);
  const [photoZipping, setPhotoZipping] = useState(false);
  const [excelDoing, setExcelDoing] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [docDescription, setDocDescription] = useState('');
  const fileInputRef = React.useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    qApi.attachments(q.id)
      .then(data => setFiles(data.files || []))
      .catch(() => setFiles([]))
      .finally(() => setLoading(false));
  }, [q.id]);
  useEffect(() => { load(); }, [load]);

  async function handleZip() {
    setZipping(true);
    try {
      const { blob, filename } = await qApi.attachmentsZip(q.id);
      triggerBlobDownload(blob, filename);
    } catch { toast?.(t('downloads.error_zip'), 'error'); }
    finally { setZipping(false); }
  }

  // Pacote só com as fotos — disponível em qualquer fase, inclusive após entrega
  async function handlePhotoZip() {
    setPhotoZipping(true);
    try {
      const { blob, filename } = await qApi.attachmentsZip(q.id, 'both', 'photos');
      triggerBlobDownload(blob, filename);
    } catch { toast?.(t('downloads.error_zip'), 'error'); }
    finally { setPhotoZipping(false); }
  }

  // O Excel sai no idioma e na moeda escolhidos no modal (igual à cotação)
  // Anexa documentos ao registro — vale em qualquer fase, inclusive depois de
  // entregue (invoice que chega atrasada, BL, packing list…).
  async function handleUpload(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      await ordersApi.uploadDocuments(q.id, files, docDescription.trim());
      toast?.(t('downloads.upload_ok'), 'success');
      setDocDescription('');
      load();
      onUploaded?.();
    } catch (err) {
      toast?.(err.response?.data?.error || t('downloads.upload_error'), 'error');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleExcel(currency_mode, lang) {
    setExcelDoing(true);
    try {
      const { blob, filename } = await qApi.export(q.id, currency_mode, lang);
      triggerBlobDownload(blob, filename);
      setShowExport(false);
    } catch { toast?.(t('downloads.error_excel'), 'error'); }
    finally { setExcelDoing(false); }
  }

  const groups = {};
  (files || []).forEach(f => { (groups[f.source] = groups[f.source] || []).push(f); });
  const sourceKeys = Object.keys(groups).sort((a, b) => (SOURCE_META[a]?.order || 9) - (SOURCE_META[b]?.order || 9));
  const photoCount = (files || []).filter(f => IMG_RE.test(f.original_name || f.filename || '')).length;

  const isOrder = q.order_type && q.order_type !== 'quotation';

  return (
    <div className="card no-print" style={{ padding: 16, marginBottom: 16 }}>
      {showExport && (
        <ExportOptionsModal
          kind={isOrder ? 'order' : 'quotation'}
          currency={q.currency}
          multiplier={q.trading_cost_multiplier || q.other_costs_multiplier}
          exporting={excelDoing}
          onExport={handleExcel}
          onClose={() => setShowExport(false)}
        />
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-500)' }}>📥 {t('downloads.title')}</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowExport(true)} disabled={excelDoing}>
            {excelDoing ? '⏳ ...' : `📊 ${t('downloads.excel_btn')}`}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={handlePhotoZip} disabled={photoZipping || photoCount === 0}
            title={t('downloads.photos_hint')}>
            {photoZipping ? '⏳ ...' : `📷 ${t('downloads.photos_btn')}${photoCount ? ` (${photoCount})` : ''}`}
          </button>
          <button className="btn btn-accent btn-sm" onClick={handleZip} disabled={zipping || (files || []).length === 0}>
            {zipping ? '⏳ ...' : `🗜️ ${t('downloads.zip_btn')}`}
          </button>
        </div>
      </div>

      {/* Anexar documentos (invoice, packing list, BL…) em qualquer fase */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12,
        padding: '10px 12px', background: 'var(--gray-50)', borderRadius: 'var(--radius)' }}>
        <input
          value={docDescription}
          onChange={e => setDocDescription(e.target.value)}
          placeholder={t('downloads.doc_description')}
          style={{ flex: '1 1 220px', minWidth: 160, padding: '6px 9px', fontSize: 12,
            border: '1px solid var(--gray-200)', borderRadius: 'var(--radius)' }} />
        <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={handleUpload} />
        <button className="btn btn-ghost btn-sm" disabled={uploading}
          onClick={() => fileInputRef.current?.click()}>
          {uploading ? '⏳ ...' : `📤 ${t('downloads.upload_btn')}`}
        </button>
      </div>

      {loading ? (
        <div style={{ fontSize: 13, color: 'var(--gray-400)' }}>⏳ {t('common.loading')}</div>
      ) : (files || []).length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--gray-400)' }}>{t('downloads.empty')}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {sourceKeys.map(src => (
            <div key={src}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                {SOURCE_META[src]?.icon} {groups[src][0].source_label} ({groups[src].length})
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {groups[src].map((f, i) => {
                  const isImg = IMG_RE.test(f.original_name || f.filename || '');
                  return (
                    <div key={i}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: '1px solid var(--gray-200)', borderRadius: 'var(--radius)', background: 'var(--gray-50)', maxWidth: 300 }}>
                      {/* Clicar no cartão BAIXA o arquivo (funciona em qualquer fase, inclusive após entregue) */}
                      <a href={f.url} download={f.original_name}
                        title={[t('downloads.download_file'), f.original_name, f.context].filter(Boolean).join(' · ')}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', minWidth: 0, flex: 1 }}>
                        {isImg
                          ? <img src={f.url} alt="" style={{ width: 34, height: 34, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />
                          : <span style={{ fontSize: 24, flexShrink: 0 }}>📄</span>}
                        <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                          <span style={{ fontSize: 12, color: 'var(--navy)', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.original_name}</span>
                          {f.context ? <span style={{ fontSize: 10, color: 'var(--gray-400)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.context}</span> : null}
                        </span>
                      </a>
                      {/* ...e a lupa abre em tamanho original numa aba nova */}
                      <a href={f.url} target="_blank" rel="noreferrer"
                        title={isImg ? t('downloads.open_photo') : t('downloads.open_file')}
                        style={{ textDecoration: 'none', fontSize: 14, color: 'var(--gray-400)', flexShrink: 0, padding: '0 2px' }}>
                        🔍
                      </a>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
